import { useState, useEffect } from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button, CircularProgress } from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useGetIdentity, useGetOne, useRecordContext } from 'react-admin';
import {
    RatePdfDocument,
    PdfLine,
    PdfDiscountInfo,
} from '../hail-pricing-tool/RatePdfDocument';
import { RateData } from '../hail-pricing-tool/types';
import { Quote } from '../types';
import { supabase } from '../providers/supabase/supabase';
import { stateAbbreviationMap } from '../utils/stateAbbreviations';

type DiscountLookup = {
    aip: string;
    state: string;
    has_discount: boolean;
    discount: number | null;
    discount_deadline: string | null;
    excluded_crops: string[];
    excluded_products: string[];
};

/**
 * Maps stored QuoteLineRate data back to the RateData shape
 * expected by RatePdfDocument.
 */
const mapQuoteLinesToPdf = (
    record: Quote
): {
    lines: PdfLine[] | undefined;
    rates: RateData[];
    applicableHailProducts: string[];
    formData: {
        state: string;
        county: string;
        township: string;
        range: string;
        crop: string;
        insuredName: string;
        ipa: number;
        selectedHailProducts: string[];
        selectedCarriers: string[];
    };
} => {
    const quoteLines = record.quote_lines ?? [];
    const products = record.selectedHailProducts ?? [];

    if (quoteLines.length === 0) {
        return {
            lines: undefined,
            rates: [],
            applicableHailProducts: products,
            formData: {
                state: record.state ?? '',
                county: record.county ?? '',
                township: record.township ?? '',
                range: record.range ?? '',
                crop: record.crop ?? '',
                insuredName: record.name ?? '',
                ipa: 1,
                selectedHailProducts: products,
                selectedCarriers: record.selectedCarriers ?? [],
            },
        };
    }

    // Build PdfLine[] from quote_lines
    const lines: PdfLine[] = quoteLines.map(ql => ({
        label: `${ql.county} - ${ql.crop} - ${ql.township} - ${ql.range} - IPA $${Number(ql.ipa).toFixed(2)}`,
        ipa: ql.ipa,
        rates: ql.rates.map(
            r =>
                ({
                    carrier: r.carrier,
                    form: r.displayForm || r.originalForm,
                    selected_rate: r.selected_rate,
                }) as RateData
        ),
        selectedCarrier: ql.selectedCarrier ?? null,
        selectedProducts: ql.selectedProducts ?? [],
    }));

    // Flatten all rates for the single-table fallback
    const allRates: RateData[] = quoteLines.flatMap(ql =>
        ql.rates.map(
            r =>
                ({
                    carrier: r.carrier,
                    form: r.displayForm || r.originalForm,
                    selected_rate: r.selected_rate,
                }) as RateData
        )
    );

    const first = quoteLines[0];

    return {
        lines,
        rates: allRates,
        applicableHailProducts: products,
        formData: {
            state: first.state || record.state || '',
            county: first.county || record.county || '',
            township: first.township || record.township || '',
            range: first.range || record.range || '',
            crop: first.crop || record.crop || '',
            insuredName: record.name ?? '',
            ipa: first.ipa ?? 1,
            selectedHailProducts: products,
            selectedCarriers: record.selectedCarriers ?? [],
        },
    };
};

/**
 * Build discount info map from raw discount data.
 */
const buildDiscountInfo = (
    data: DiscountLookup[],
    crop?: string
): Map<string, PdfDiscountInfo> => {
    const map = new Map<string, PdfDiscountInfo>();
    data.forEach(d => {
        if (d.has_discount && d.discount != null) {
            // Skip if this crop is excluded from this AIP's discount
            if (crop && d.excluded_crops?.includes(crop)) return;
            const info: PdfDiscountInfo = {
                fraction: d.discount,
                pct:
                    (d.discount * 100).toFixed(
                        Number.isInteger(d.discount * 100) ? 0 : 1
                    ) + '%',
                due: d.discount_deadline || '',
                excluded_products: d.excluded_products ?? [],
            };
            map.set(d.aip, info);
            // Also map alternate carrier key forms so lookups
            // work regardless of how the carrier is stored in
            // quote_lines (e.g. "Pro Ag" vs "proAg").
            if (d.aip === 'Pro Ag') {
                map.set('proAg', info);
                map.set('ProAg', info);
            }
        }
    });
    return map;
};

/**
 * Generate PDF download button for a quote.
 * Reads the quote record from context and builds the PDF document
 * from stored quote_lines data.
 */
export const GeneratePdfButton = () => {
    const record = useRecordContext<Quote>();
    const { identity } = useGetIdentity();
    const { data: account } = useGetOne(
        'accounts',
        { id: record?.account_id as number },
        { enabled: !!record?.account_id }
    );

    const showDiscount =
        record?.paymentMethod === 'Cash Discount (when applicable)';
    const [discountMap, setDiscountMap] = useState<
        Map<string, PdfDiscountInfo>
    >(new Map());
    const [discountLoaded, setDiscountLoaded] = useState(false);

    useEffect(() => {
        if (!showDiscount || !record?.state) {
            setDiscountMap(new Map());
            setDiscountLoaded(true);
            return;
        }
        const abbr = stateAbbreviationMap[record.state];
        if (!abbr) {
            setDiscountMap(new Map());
            setDiscountLoaded(true);
            return;
        }
        setDiscountLoaded(false);
        supabase
            .from('discount_manager')
            .select(
                'aip, state, has_discount, discount, discount_deadline, excluded_crops, excluded_products'
            )
            .eq('state', abbr)
            .then(({ data, error }) => {
                if (error) {
                    console.error('Error fetching discounts for PDF:', error);
                    setDiscountMap(new Map());
                } else {
                    setDiscountMap(
                        buildDiscountInfo(
                            (data as DiscountLookup[]) ?? [],
                            record?.crop
                        )
                    );
                }
                setDiscountLoaded(true);
            });
    }, [showDiscount, record?.state]);

    if (!record || !identity || !discountLoaded) return null;

    const hasRates =
        record.quote_lines &&
        record.quote_lines.length > 0 &&
        record.quote_lines.some(ql => ql.rates.length > 0);

    if (!hasRates) {
        return (
            <Button size="small" startIcon={<PictureAsPdfIcon />} disabled>
                No rates to export
            </Button>
        );
    }

    const { lines, rates, applicableHailProducts, formData } =
        mapQuoteLinesToPdf(record);

    const baseUrl =
        typeof window !== 'undefined'
            ? window.location.origin
            : 'https://hailhelper.app';
    const cropWestLogoUrl = `${baseUrl}/CropWest_logo.png`;

    const fileName = `${record.name || 'Quote'}_${new Date().toISOString().slice(0, 10)}.pdf`;

    // Convert Map to plain object for serialization to PDF component
    const discountInfoObj: Record<string, PdfDiscountInfo> = {};
    discountMap.forEach((val, key) => {
        discountInfoObj[key] = val;
    });

    return (
        <PDFDownloadLink
            document={
                <RatePdfDocument
                    formData={formData}
                    insuredName={formData.insuredName}
                    accountName={account?.name ?? ''}
                    ipa={formData.ipa}
                    rates={rates}
                    lines={lines}
                    applicableHailProducts={applicableHailProducts}
                    identity={identity}
                    cropWestLogoUrl={cropWestLogoUrl}
                    discountInfo={showDiscount ? discountInfoObj : undefined}
                    selectedEndorsements={record.selectedEndorsements}
                    endorsementRateLookup={record.endorsementRateLookup}
                />
            }
            fileName={fileName}
            style={{ textDecoration: 'none' }}
        >
            {({ loading }) => (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} />
                        ) : (
                            <PictureAsPdfIcon />
                        )
                    }
                    disabled={loading}
                >
                    {loading ? 'Generating...' : 'Generate PDF'}
                </Button>
            )}
        </PDFDownloadLink>
    );
};
